import React, {
    useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import PayseraTicketsLogo from '../Header/PayseraTicketsLogo';
import CategoriesColumn from './CategoriesColumn';
import LinkColumn from './LinkColumn';
import NewsletterBanner, { NEWSLETTER_STATUS } from './NewsletterBanner';
import SocialIcons from './SocialIcons';
import FooterAccordionPanel from './FooterAccordionPanel';
import subscribeToNewsletter from '../../services/subscribeToNewsletter';
import { COOKIE_SETTINGS_PORTAL_ID } from '%paysera_service_agreement/js/CookiesUserAgreement/config/cookieSettingsPortal';
import { footerTranslationsShape, footerLinksShape } from './propTypes';
import { LINK_TEXT_DECORATION } from '../highContrastSx';

const FOOTER_SX = {
    mt: 4,
};

const BODY_SX = {
    bgcolor: 'grey.50',
};

const BODY_CONTAINER_SX = {
    py: { xs: 0.5, md: 4 },
};

const BODY_GRID_SX = {
    display: { md: 'grid' },
    gridTemplateColumns: { md: '1fr 1fr 1fr' },
    gap: { md: 6 },
    mt: { md: 4 },
};

export const COOKIE_SETTINGS_PORTAL_SX = theme => ({
    display: 'block',
    '& button': {
        font: 'inherit',
        fontSize: theme.typography.pxToRem(14),
        color: 'text.secondary',
        textDecoration: LINK_TEXT_DECORATION,
        background: 'transparent',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        textAlign: 'left',
        '&:hover': {
            color: 'primary.main',
            textDecoration: 'underline',
        },
    },
});

const BOTTOM_BAR_SX = {
    bgcolor: 'background.paper',
    borderTop: '1px solid',
    borderColor: 'divider',
};

const BOTTOM_BAR_CONTAINER_SX = {
    py: 2.5,
};

const BRAND_ROW_SX = {
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    alignItems: { xs: 'flex-start', md: 'center' },
    gap: 2,
    mb: 1.5,
};

const BRAND_CLUSTER_SX = {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
};

const BRAND_DIVIDER_SX = {
    width: '1px',
    height: 16,
    bgcolor: 'grey.200',
};

const COPYRIGHT_ROW_SX = {
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    alignItems: { sm: 'center' },
    justifyContent: 'space-between',
    gap: 0.5,
    pt: 1.5,
    borderTop: '1px solid',
    borderColor: 'grey.100',
};

export const FINE_PRINT_SX = theme => ({
    fontSize: theme.typography.pxToRem(12),
    color: 'text.secondary',
    margin: 0,
});

const FOR_BUYERS_LINK_KEYS = [
    ['footerMyTickets', 'myTickets'],
    ['footerGetYourTicket', 'getYourTicket'],
    ['footerPrivacyPolicy', 'privacyPolicy'],
    ['footerTermsOfService', 'termsOfService'],
    ['footerContacts', 'contacts'],
];

const FOR_ORGANISERS_LINK_KEYS = [
    ['footerCreateEvent', 'createEvent'],
    ['footerAgreementForOrganisers', 'agreementForOrganisers'],
    ['footerPersonalDataProcessing', 'personalDataProcessing'],
    ['footerPrivacyPolicy', 'privacyPolicy'],
    ['footerTermsOfService', 'termsOfService'],
    ['footerAffiliate', 'affiliate'],
    ['footerLogos', 'logos'],
    ['footerAdvertisement', 'advertisement'],
    ['footerReport', 'report'],
    ['footerValidateTickets', 'validateTickets'],
    ['footerPricing', 'pricing'],
    ['footerContacts', 'organiserContacts'],
];

const INFORMATION_LINK_KEYS = [
    ['footerAboutSystem', 'aboutSystem'],
    ['footerPaymentServicesAgreementPrivate', 'paymentServicesAgreementPrivate'],
    ['footerPaymentServicesAgreementBusiness', 'paymentServicesAgreementBusiness'],
    ['footerFaq', 'faq'],
];

function buildExternalLinks(translations, footerLinks, linkKeys) {
    return linkKeys
        .map(([translationKey, linkKey]) => ({
            label: translations[translationKey],
            href: footerLinks[linkKey] || '',
            external: true,
        }))
        .filter(link => Boolean(link.href));
}

function buildSocialLinks(footerLinks) {
    return {
        facebook: footerLinks.facebook || '',
        instagram: footerLinks.instagram || '',
    };
}

function buildSocialLabels(translations) {
    return {
        facebook: translations.footerSocialFacebook,
        instagram: translations.footerSocialInstagram,
    };
}

function Footer({ translations, footerLinks }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [newsletterStatus, setNewsletterStatus] = useState(NEWSLETTER_STATUS.IDLE);
    const newsletterRequestRef = useRef(null);

    useEffect(() => () => {
        if (newsletterRequestRef.current) {
            newsletterRequestRef.current.abort();
        }
    }, []);

    const handleNewsletterSubmit = useCallback(async (email) => {
        if (newsletterRequestRef.current) {
            newsletterRequestRef.current.abort();
        }

        const controller = new AbortController();
        newsletterRequestRef.current = controller;
        setNewsletterStatus(NEWSLETTER_STATUS.LOADING);

        try {
            const result = await subscribeToNewsletter(email, { signal: controller.signal });
            setNewsletterStatus(result);
        } catch (error) {
            if (error.name !== 'AbortError') {
                setNewsletterStatus(NEWSLETTER_STATUS.ERROR);
            }
        }
    }, []);

    const forBuyersLinks = useMemo(
        () => buildExternalLinks(translations, footerLinks, FOR_BUYERS_LINK_KEYS),
        [translations, footerLinks],
    );
    const forOrganisersLinks = useMemo(
        () => buildExternalLinks(translations, footerLinks, FOR_ORGANISERS_LINK_KEYS),
        [translations, footerLinks],
    );
    const informationLinks = useMemo(
        () => buildExternalLinks(translations, footerLinks, INFORMATION_LINK_KEYS),
        [translations, footerLinks],
    );
    const socialLinks = buildSocialLinks(footerLinks);
    const socialLabels = buildSocialLabels(translations);

    const cookieSettingsSlot = (
        <Box
            component="span"
            id={COOKIE_SETTINGS_PORTAL_ID}
            data-testid="redesign-footer-cookie-settings"
            sx={COOKIE_SETTINGS_PORTAL_SX}
        />
    );

    return (
        <Box component="footer" sx={FOOTER_SX} data-testid="redesign-footer">
            <NewsletterBanner
                heading={translations.footerNewsletterHeading}
                description={translations.footerNewsletterDescription}
                placeholder={translations.footerNewsletterPlaceholder}
                submitLabel={translations.footerNewsletterSubmit}
                invalidEmailLabel={translations.footerNewsletterInvalidEmail}
                loadingLabel={translations.footerNewsletterLoading}
                successLabel={translations.footerNewsletterSuccess}
                errorLabel={translations.footerNewsletterError}
                consentLabel={translations.footerNewsletterConsentLabel}
                privacyPolicyLabel={translations.footerPrivacyPolicy}
                privacyPolicyHref={footerLinks.privacyPolicy || ''}
                status={newsletterStatus}
                onSubmit={handleNewsletterSubmit}
            />

            <Box sx={BODY_SX}>
                <Container sx={BODY_CONTAINER_SX}>
                    {isMobile ? (
                        <Box data-testid="redesign-footer-mobile">
                            <FooterAccordionPanel
                                heading={translations.footerCategoriesHeading}
                                testId="redesign-footer-accordion-categories"
                            >
                                <CategoriesColumn
                                    heading={translations.footerCategoriesHeading}
                                    showHeading={false}
                                />
                            </FooterAccordionPanel>
                            <FooterAccordionPanel
                                heading={translations.footerForBuyersHeading}
                                testId="redesign-footer-accordion-for-buyers"
                            >
                                <LinkColumn
                                    heading={translations.footerForBuyersHeading}
                                    links={forBuyersLinks}
                                    testId="redesign-footer-for-buyers"
                                    showHeading={false}
                                />
                            </FooterAccordionPanel>
                            <FooterAccordionPanel
                                heading={translations.footerForOrganisersHeading}
                                testId="redesign-footer-accordion-for-organisers"
                            >
                                <LinkColumn
                                    heading={translations.footerForOrganisersHeading}
                                    links={forOrganisersLinks}
                                    testId="redesign-footer-for-organisers"
                                    showHeading={false}
                                />
                            </FooterAccordionPanel>
                            <FooterAccordionPanel
                                heading={translations.footerInformationHeading}
                                testId="redesign-footer-accordion-information"
                            >
                                <LinkColumn
                                    heading={translations.footerInformationHeading}
                                    links={informationLinks}
                                    testId="redesign-footer-information"
                                    showHeading={false}
                                    slotBeforeLastLink={cookieSettingsSlot}
                                />
                            </FooterAccordionPanel>
                        </Box>
                    ) : (
                        <Box data-testid="redesign-footer-desktop">
                            <CategoriesColumn
                                heading={translations.footerCategoriesHeading}
                                gridLayout
                            />
                            <Box sx={BODY_GRID_SX}>
                                <LinkColumn
                                    heading={translations.footerForBuyersHeading}
                                    links={forBuyersLinks}
                                    testId="redesign-footer-for-buyers"
                                />
                                <LinkColumn
                                    heading={translations.footerForOrganisersHeading}
                                    links={forOrganisersLinks}
                                    testId="redesign-footer-for-organisers"
                                />
                                <LinkColumn
                                    heading={translations.footerInformationHeading}
                                    links={informationLinks}
                                    testId="redesign-footer-information"
                                    slotBeforeLastLink={cookieSettingsSlot}
                                />
                            </Box>
                        </Box>
                    )}
                </Container>
            </Box>

            <Box sx={BOTTOM_BAR_SX}>
                <Container sx={BOTTOM_BAR_CONTAINER_SX}>
                    <Box sx={BRAND_ROW_SX}>
                        <Box sx={BRAND_CLUSTER_SX}>
                            <PayseraTicketsLogo title={translations.logoAlt} />
                            <Box sx={BRAND_DIVIDER_SX} aria-hidden="true" />
                            <SocialIcons links={socialLinks} labels={socialLabels} />
                        </Box>
                    </Box>
                    <Box sx={COPYRIGHT_ROW_SX}>
                        <Box
                            component="p"
                            sx={FINE_PRINT_SX}
                            data-testid="redesign-footer-copyright"
                        >
                            {translations.footerCopyright}
                        </Box>
                        <Box
                            component="p"
                            sx={FINE_PRINT_SX}
                            data-testid="redesign-footer-powered-by"
                        >
                            {translations.footerPoweredBy}
                        </Box>
                    </Box>
                </Container>
            </Box>
        </Box>
    );
}

Footer.propTypes = {
    translations: footerTranslationsShape.isRequired,
    footerLinks: footerLinksShape.isRequired,
};

export default Footer;
